import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Layout } from "@/components/Layout";
import { Reveal } from "@/components/Reveal";
import { BookButton } from "@/components/PrimaryCTA";
import { TestimonialCarousel } from "@/components/TestimonialCarousel";
import { ReviewForm } from "@/components/ReviewForm";
import { MotionClips } from "@/components/MotionClips";
import { ConnectedSystem } from "@/components/ConnectedSystem";
import { ClassCard } from "@/components/ClassCard";
import { InterestModal } from "@/components/InterestModal";
import { CommunitySignup } from "@/components/CommunitySignup";
import { KineticBackground } from "@/components/KineticBackground";
import { HeroBackground } from "@/components/HeroBackground";
import { Wordmark } from "@/components/Wordmark";
import { api } from "@/lib/apiClient";
import { HOME, IMAGES } from "@/content";

function Hero() {
  const reduce = useReducedMotion();
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 600], [0, 90]);

  return (
    <section className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden text-center">
      <HeroBackground />


      <motion.div style={reduce ? {} : { y }} className="relative z-10 mx-auto max-w-[1200px] w-full px-5 sm:px-8 pt-24">
        <motion.img
          src="/media/logo.png"
          alt="I•AMovement"
          draggable="false"
          className="mx-auto w-[82vw] max-w-[720px] h-auto"
          initial={{ opacity: 0, y: 24, filter: "blur(10px)" }}
          animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
          transition={{ duration: 1.1, delay: 0.15, ease: [0.25, 1, 0.5, 1] }}
        />

        <motion.div
          className="font-display text-ice/90 mt-8 text-xl sm:text-2xl md:text-3xl max-w-2xl mx-auto leading-snug space-y-1.5"
          data-testid="hero-heading"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.6, duration: 0.6 }}
        >
          {HOME.hookLines.map((l, i) => (
            <motion.p key={i} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7 + i * 0.28, duration: 0.8, ease: [0.25, 1, 0.5, 1] }}>{l}</motion.p>
          ))}
        </motion.div>
        <motion.p
          className="text-mist mt-4 text-base sm:text-lg max-w-xl mx-auto leading-relaxed"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.7, duration: 0.9 }}
        >
        </motion.p>

        <motion.div className="mt-10 flex flex-wrap items-center justify-center gap-5"
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.2, duration: 0.7 }}>
          <BookButton />
          <Link to="/the-reconnection" data-testid="hero-why-link" className="link-underline text-ice/85 hover:text-white inline-flex items-center gap-2">
            Why we exist <ArrowRight size={16} />
          </Link>
        </motion.div>
      </motion.div>

      <motion.div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.6, duration: 1 }}>
        <span className="label">Scroll</span>
        <motion.div className="h-10 w-[1px] bg-gradient-to-b from-ice/60 to-transparent"
          animate={reduce ? {} : { scaleY: [0.4, 1, 0.4], originY: 0 }} transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }} />
      </motion.div>
    </section>
  );
}

export default function Home() {
  const [classes, setClasses] = useState([]);
  const [locations, setLocations] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [interest, setInterest] = useState(null);

  useEffect(() => {
    api.get("/classes").then((r) => setClasses(r.data)).catch(() => {});
    api.get("/locations").then((r) => setLocations(r.data)).catch(() => {});
    api.get("/testimonials").then((r) => setTestimonials(r.data)).catch(() => {});
  }, []);

  const preview = classes.slice(0, 5);

  return (
    <Layout page="home">
      <Hero />

      {/* Intro editorial */}
      <section className="relative py-24 sm:py-36">
        <div className="mx-auto max-w-4xl px-5 sm:px-8">
          <Reveal><span className="label">The idea</span></Reveal>
          {HOME.body.map((p, i) => (
            <Reveal key={i} delay={i * 0.05}>
              <p className={`mt-6 leading-relaxed ${i === 0 ? "font-display text-xl sm:text-2xl font-medium text-white leading-snug" : "text-ice/75 text-lg"}`}>
                {p}
              </p>
            </Reveal>
          ))}
          <Reveal>
            <Link to="/the-reconnection" data-testid="why-exists-link"
              className="group inline-flex items-center gap-3 mt-12 font-display text-2xl sm:text-3xl font-bold text-white hover:text-cyan transition-colors">
              This is why I•AMovement exists
              <ArrowRight className="group-hover:translate-x-2 transition-transform" size={28} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* Connected system */}
      <section className="relative py-16 sm:py-24">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8"><ConnectedSystem /></div>
      </section>

      {/* Testimonials */}
      <section className="relative py-24 sm:py-32 overflow-hidden">
        <div className="absolute inset-0 mesh-top opacity-50" />
        <div className="relative mx-auto max-w-[1400px] px-5 sm:px-8">
          <Reveal className="text-center mb-14"><span className="label">In their words</span></Reveal>
          <TestimonialCarousel items={testimonials} />
          <div className="mt-12"><ReviewForm page="home" /></div>

          {/* Movement clips sit alongside the testimonials */}
          <Reveal className="mt-20 mb-8">
            <span className="label">In motion</span>
            <h2 className="font-display text-3xl sm:text-5xl font-bold text-white mt-3">Movement, up close.</h2>
          </Reveal>
          <MotionClips clips={[
            { label: "Strength in motion", video: "/media/videos/110.mp4", poster: "/media/posters/110.jpg" },
            { label: "Mobility & injury prevention", video: "/media/videos/109.mp4", poster: "/media/posters/109.jpg" },
            { label: "Grounding & body awareness", video: "/media/videos/104.mp4", poster: "/media/posters/104.jpg" },
            { label: "Coordination & rhythm", video: "/media/videos/108.mp4", poster: "/media/posters/108.jpg" },
            { label: "Balance & efficiency in movement", video: "/media/videos/105.mp4", poster: "/media/posters/105.jpg" },
            { label: "Tension release", video: "/media/videos/103.mp4", poster: "/media/posters/103.jpg" },
            { label: "Full-body movement patterns", video: "/media/videos/106.mp4", poster: "/media/posters/106.jpg" },
            { label: "Nervous system regulation", video: "/media/videos/102.mp4", poster: "/media/posters/102.jpg" },
          ]} />
        </div>
      </section>

      {/* Reconnection preview */}
      <section className="relative py-16 sm:py-24">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
          <Link to="/the-reconnection" data-testid="reconnection-preview"
            className="group relative block overflow-hidden rounded-3xl border border-white/10 min-h-[340px]">
            <img src={IMAGES.motionBlur} alt="Abstract motion" className="absolute inset-0 h-full w-full object-cover opacity-60 group-hover:opacity-75 group-hover:scale-105 transition-all duration-700" />
            <div className="absolute inset-0 bg-gradient-to-r from-ink-900 via-ink-900/70 to-transparent" />
            <div className="relative p-8 sm:p-16 flex flex-col justify-end min-h-[340px]">
              <span className="label">Why</span>
              <h2 className="font-display text-4xl sm:text-6xl font-black text-white mt-3">The Reconnection</h2>
              <p className="text-ice/75 max-w-lg mt-4">Every person lives in a body, yet few are ever taught how to understand or care for it.</p>
              <span className="inline-flex items-center gap-2 text-cyan mt-6 font-medium">Read the story <ArrowRight className="group-hover:translate-x-2 transition-transform" size={18} /></span>
            </div>
          </Link>
        </div>
      </section>

      {/* Class preview */}
      <section className="relative py-16 sm:py-24">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
          <Reveal className="flex items-end justify-between mb-10 flex-wrap gap-4">
            <div>
              <span className="label">The strands</span>
              <h2 className="font-display text-3xl sm:text-5xl font-bold text-white mt-3">Each class trains a<br className="hidden sm:block" /> different part of the system.</h2>
            </div>
            <Link to="/classes" data-testid="all-classes-link" className="link-underline text-ice/80 hover:text-white inline-flex items-center gap-2">All classes <ArrowRight size={16} /></Link>
          </Reveal>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 auto-rows-fr">
            {preview.map((c) => <ClassCard key={c.slug} cls={c} onInterest={setInterest} />)}
          </div>
        </div>
      </section>

      {/* Science preview */}
      <section className="relative py-16 sm:py-24">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8">
          <Link to="/rooted-in-the-body" data-testid="science-preview"
            className="group relative block overflow-hidden rounded-3xl border border-white/10 min-h-[300px]">
            <img src={IMAGES.scienceNeural} alt="Neural pathways" className="absolute inset-0 h-full w-full object-cover opacity-65 group-hover:opacity-80 transition-opacity duration-700" />
            <div className="absolute inset-0 bg-gradient-to-l from-ink-900 via-ink-900/60 to-transparent" />
            <div className="relative p-8 sm:p-16 flex flex-col justify-end items-end text-right min-h-[300px]">
              <span className="label">Science</span>
              <h2 className="font-display text-4xl sm:text-6xl font-black text-white mt-3">Rooted in the Body</h2>
              <p className="text-ice/75 max-w-lg mt-4">Every class draws on real physiology and neuroscience.</p>
              <span className="inline-flex items-center gap-2 text-cyan mt-6 font-medium">Explore the science <ArrowRight className="group-hover:translate-x-2 transition-transform" size={18} /></span>
            </div>
          </Link>
        </div>
      </section>

      {/* Big book CTA */}
      <section className="relative py-24 sm:py-36 text-center overflow-hidden">
        <div className="absolute inset-0 mesh-top" />
        <div className="relative mx-auto max-w-4xl px-5 sm:px-8">
          <Reveal>
            <h2 className="font-display font-black text-white leading-[1.02]" style={{ fontSize: "clamp(2.6rem, 8vw, 6rem)" }}>
              Come and be<br />part of it.
            </h2>
            <div className="mt-10 flex justify-center"><BookButton /></div>
          </Reveal>
        </div>
      </section>

      {/* Community */}
      <section className="relative pb-28">
        <div className="mx-auto max-w-[1400px] px-5 sm:px-8"><CommunitySignup locations={locations} /></div>
      </section>

      <InterestModal cls={interest} onClose={() => setInterest(null)} />
    </Layout>
  );
}
